import { get, isEmpty, pickBy } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../../actions'
import { ExperimentTypes } from '../../../../typeModules/events'
import { Application } from '../../../../utilities/application/application'
import { VehicleType } from '../../vehicle-details/VehicleDetailsForm'
import AbnPage from './AbnPage'
import VehicleDetails from './VehicleDetails'

interface AustraliaAbnOptionalProps {
  uniqueLink: string
  application?: Application
  dasherId?: number
  isAbnRevisited?: boolean
  vehicleTypes: VehicleType[]
  experiments?: ExperimentTypes
  submitBackgroundCheck?: (
    uniqueLink: string,
    backgroundInfo: object,
    requestedBgCheckCopy?: boolean
  ) => Promise<void>
  submitAbn?: (australianBusinessNumber: string) => Promise<void>
  updateDasherApplicant?: (
    uniqueLink: string,
    data: Record<string, string>
  ) => Promise<void>
  createDasherAccount?: (
    uniqueLink: string,
    suppressError?: boolean
  ) => Promise<void>
  loadApplication?: (uniqueLink: string) => Promise<void>
  loadApplicationAndCreatePaymentAccount?: (
    uniqueLink: string,
    data: Record<string, string>
  ) => Promise<void>
}

export enum AusBackgroundCheckStep {
  VEHICLE_DETAILS,
  ABN,
}

function AustraliaAbnOptional(props: AustraliaAbnOptionalProps) {
  const {
    application,
    dasherId,
    isAbnRevisited,
    updateDasherApplicant,
    createDasherAccount,
    submitBackgroundCheck,
    submitAbn,
    loadApplicationAndCreatePaymentAccount,
    loadApplication,
  } = props

  const hasDob = !!application.dateOfBirth

  const handleSubmit = async (
    uniqueLink: string,
    backgroundInfo: Record<string, string>,
    backgroundCheckStep: AusBackgroundCheckStep
  ) => {
    // Ready payload by removing empty keys
    const filteredBackgroundInfo = pickBy(backgroundInfo, val => val !== '')

    if (backgroundCheckStep === AusBackgroundCheckStep.VEHICLE_DETAILS) {
      if (!isEmpty(filteredBackgroundInfo)) {
        await updateDasherApplicant(uniqueLink, filteredBackgroundInfo)
      }

      await loadApplication(uniqueLink)
      return
    }

    const { australianBusinessNumber } = filteredBackgroundInfo
    if (australianBusinessNumber) {
      await submitAbn(australianBusinessNumber)
    }

    // ABN revisit: skip creating dasher, submit bgc and payment account
    // as they are called the first time ABN page is skipped
    if (isAbnRevisited) {
      await loadApplication(uniqueLink)
      return
    }

    if (!dasherId) {
      await createDasherAccount(uniqueLink)
    }

    await submitBackgroundCheck(uniqueLink, filteredBackgroundInfo)

    const bankOnboardingExperiment = get(
      props.experiments,
      'bank_onboarding',
      false
    )

    // Reload application object after successful background check submit
    if (bankOnboardingExperiment) {
      await loadApplicationAndCreatePaymentAccount(
        uniqueLink,
        filteredBackgroundInfo
      )
    } else {
      await loadApplication(uniqueLink)
    }
  }

  if (hasDob || isAbnRevisited) {
    return (
      <AbnPage
        uniqueLink={props.uniqueLink}
        onSubmit={handleSubmit}
        required={isAbnRevisited}
      />
    )
  }

  return (
    <VehicleDetails
      vehicleTypes={props.vehicleTypes}
      uniqueLink={props.uniqueLink}
      onSubmit={handleSubmit}
    />
  )
}

const mapStateToProps = state => ({
  application: state.application,
  dasherId: state.dasherId,
  vehicleTypes: state.vehicleTypes,
  experiments: state.experiments,
})

const mapDispatchToProps = {
  submitBackgroundCheck: actions.submitBackgroundCheck,
  submitAbn: actions.submitAbnRequest,
  updateDasherApplicant: actions.updateDasherApplicant,
  createDasherAccount: actions.createDasherAccount,
  loadApplication: actions.loadApplication,
  loadApplicationAndCreatePaymentAccount:
    actions.loadApplicationAndCreatePaymentAccount,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AustraliaAbnOptional)
